import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLiveDevices, useLiveReady } from "../../../live/LiveProvider";
import { Button } from "../../../ui/Button";
import { ErrorMessage } from "../../../ui/ErrorMessage";
import { Loading } from "../../../ui/Spinner";
import { WidgetView } from "../../../widgets/registry";
import { ActionDialog } from "../actions/ActionDialog";
import { QuickAction, quickAction } from "../actions/QuickAction";
import { useDeviceActions } from "../actions/useDeviceActions";
import { profileQuery } from "./profile";
import { devicePageQuery, latestQuery } from "./queries";
import { sheetTile, widgetBindings, widgetInput } from "./toWidgets";

/**
 * The device sheet a map marker opens (WEB-F-DEV-05, WEB-MAP-03): the device's small home tile as its type has it now,
 * live while the sheet is open, and a link to its page. It reads the device's page document for the tile and the
 * points' names and units.
 */
export function DeviceSheet({
  orgId,
  deviceId,
  name,
  now,
  onClose,
  children,
}: {
  orgId: string;
  deviceId: string | undefined;
  name: string | undefined;
  now: Date;
  onClose: () => void;
  /** What the page adds under the tile (the Map screen: Track and Place on map, maps-2). */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const title = useId();
  const open = !!deviceId;

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={title}
      className="m-auto w-full max-w-sm rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      {deviceId && (
        <div className="p-6">
          {/* The tile shows the name; the heading names the dialog. */}
          <h2 id={title} className="sr-only">
            {name ?? deviceId}
          </h2>
          <SheetTile orgId={orgId} deviceId={deviceId} now={now} onClose={onClose} />
          {children && <div className="mt-4 flex flex-col items-start gap-3">{children}</div>}
          <div className="mt-6 flex items-center justify-between gap-3">
            <Link
              to="/devices/$deviceId"
              params={{ deviceId }}
              onClick={onClose}
              className="text-sm text-brand underline-offset-2 hover:underline"
            >
              {t("deviceSheet.open")}
            </Link>
            <Button variant="secondary" onClick={onClose}>
              {t("deviceSheet.close")}
            </Button>
          </div>
        </div>
      )}
    </dialog>
  );
}

function SheetTile({
  orgId,
  deviceId,
  now,
  onClose,
}: {
  orgId: string;
  deviceId: string;
  now: Date;
  onClose: () => void;
}) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const page = useQuery(devicePageQuery(orgId, deviceId, i18n.language));
  const tile = useMemo(() => (page.data ? sheetTile(deviceId, page.data) : undefined), [deviceId, page.data]);
  const bindings = useMemo(() => (tile ? widgetBindings(tile) : []), [tile]);
  const live = useLiveReady([deviceId]);
  useLiveDevices([deviceId]);
  const latest = useQuery(latestQuery(orgId, bindings, live));
  // The card's quick action works here too (device-controls WEB-CMD-01).
  // Its page document is read only when the card has a quick action.
  const profile = useQuery({
    ...profileQuery(orgId, deviceId, i18n.language),
    enabled: !!page.data && !!quickAction(deviceId, page.data),
  });
  const actions = useDeviceActions(
    orgId,
    () => profile.data,
    (_d, k) => page.data?.actions[k],
  );

  if (page.isPending) return <Loading />;
  if (page.isError) return <ErrorMessage error={page.error} onRetry={() => void page.refetch()} />;
  const input = widgetInput(deviceId, page.data, latest.data);
  return (
    <>
      {tile && (
        <WidgetView
          widget={tile}
          input={input}
          now={now}
          readOnly
          onOpenDevice={(id) => {
            onClose();
            void navigate({ to: "/devices/$deviceId", params: { deviceId: id } });
          }}
        />
      )}
      <div className="mt-4">
        <QuickAction device={deviceId} page={page.data} actions={actions} now={now} />
      </div>
      <ActionDialog actions={actions} nameOf={() => page.data.device.name} />
      {latest.isError && (
        <div className="mt-4">
          <ErrorMessage error={latest.error} onRetry={() => void latest.refetch()} />
        </div>
      )}
    </>
  );
}
